// The members grid shared by the game Members tab and the organization Members page (spec/dashboard.md section 12.4):
// rows `member-<userId>` with email, name, a role select that saves on change, since, until (M7 slice 3: "until <instant>",
// or a red "expired" for a row the sweep has not removed yet), Access until… (`access-until-dialog`: PATCH with expiresAt,
// cleared → null), Remove (confirm); Add… opens the `add-member-dialog` (email, role, optional access until); an unknown
// email (`404`) is said in the dialog. The role selects name each role and describe it (`access/roles.ts`): the chosen role's
// description under the Add dialog's select, as the title of a row's select and its options, and "What can each role do?"
// under the table's head.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/Banner';
import { Badge } from '../../ui/Chip';
import { UserPlus, Users } from '../../ui/icons';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { TextInput } from '../../ui/inputs';
import { RoleHelp, RoleSelect } from '../../access/RoleSelect';
import { roleDescription, roleLabel, type RoleKind } from '../../access/roles';
import { Modal } from '../../ui/Modal';
import type { ZoneContext } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { asApiError, isoToSeconds, secondsToIso } from './keys';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import '../org/org.css';

export interface MemberRow {
  userId: string;
  email: string;
  displayName: string;
  role: string | null;
  /** time-boxed access (M7 slice 3); null = no end */
  expiresAt?: string | null;
  version: number | string;
  createdAt: string | null;
}

export interface MembersTableProps {
  rows: MemberRow[];
  roles: string[];
  /** which role words describe the roles (game or organization); inferred from the roles when absent */
  roleKind?: RoleKind;
  /** the permission the caller lacks for the writes, or null */
  missing: string | null;
  loading?: boolean;
  caption: string;
  emptyText: string;
  when: (v: string | null | undefined) => string;
  ctx?: ZoneContext;
  onRoleChange: (m: MemberRow, role: string) => Promise<void>;
  /** sets (an instant) or clears (null) the end of the member's access */
  onExpiryChange: (m: MemberRow, expiresAt: string | null) => Promise<void>;
  onRemove: (m: MemberRow) => Promise<void>;
  onAdd: (email: string, role: string, expiresAt: string | null) => Promise<void>;
  /** the role preselected in the Add dialog */
  defaultRole: string;
  /** more toolbar controls next to Add… (Invite…) */
  toolbar?: ReactNode;
}

/** "until <instant>", "expired" (red) or "—". */
export function UntilCell({ expiresAt, when, now = Date.now() }: { expiresAt: string | null | undefined; when: (v: string | null | undefined) => string; now?: number }) {
  if (!expiresAt) return <span className="muted">—</span>;
  if (Date.parse(expiresAt) <= now)
    return (
      <Badge tone="danger" title={`Expired ${when(expiresAt)}; the hourly sweep removes the row.`}>
        expired
      </Badge>
    );
  return <span>until {when(expiresAt)}</span>;
}

export function MembersTable({ rows, roles, missing, loading, caption, emptyText, when, ctx, onRoleChange, onExpiryChange, onRemove, onAdd, defaultRole, toolbar, roleKind }: MembersTableProps) {
  const kind: RoleKind = roleKind ?? (roles.includes('owner') ? 'org' : 'game');
  const toasts = useToasts();
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<MemberRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const blockedTitle = missing ? `You lack the '${missing}' permission.` : undefined;

  const changeRole = async (m: MemberRow, role: string) => {
    if (role === m.role) return;
    setBusy(m.userId);
    try {
      await onRoleChange(m, role);
      toasts.success(`${m.email} is now ${roleLabel(kind, role)}.`);
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.isVersionConflict ? 'Someone changed this member meanwhile; the list was re-read.' : err.message, { requestId: err.requestId });
    } finally {
      setBusy(null);
    }
  };

  const remove = async (m: MemberRow) => {
    if (!(await confirm({ title: `Remove ${m.email}?`, body: 'They lose this role at once.', confirmLabel: 'Remove', tone: 'danger' }))) return;
    setBusy(m.userId);
    try {
      await onRemove(m);
      toasts.success(`${m.email} removed.`);
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pane settings-block">
      <div className="pane-head">
        <h2>{caption}</h2>
        {rows.length > 0 && <span className="muted">{rows.length}</span>}
        <div className="spacer" />
        {toolbar}
        <Button variant="primary" icon={<UserPlus aria-hidden="true" />} onClick={() => setAdding(true)} missingPermission={missing}>
          Add…
        </Button>
      </div>
      <div className="role-help-bar">
        <RoleHelp kind={kind} />
      </div>
      <div className="pane-body flush">
      {loading && rows.length === 0 ? (
        <SkeletonRows />
      ) : rows.length === 0 ? (
        <EmptyState icon={<Users />} title={emptyText} body="Add someone who already has an account, or invite them by email." />
      ) : (
        <table className="pane-table settings-table">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Name</th>
              <th scope="col">Role</th>
              <th scope="col">Since</th>
              <th scope="col">Until</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.userId} data-testid={`member-${m.userId}`}>
                <td className="member-email">{m.email}</td>
                <td>{m.displayName}</td>
                <td>
                  <RoleSelect kind={kind} roles={roles} size="s" aria-label={`Role of ${m.email}`} value={m.role ?? ''} disabled={Boolean(missing) || busy === m.userId} title={blockedTitle ?? roleDescription(kind, m.role)} onChange={(e) => void changeRole(m, e.target.value)} />
                </td>
                <td>{when(m.createdAt)}</td>
                <td data-testid={`until-${m.userId}`}>
                  <UntilCell expiresAt={m.expiresAt} when={when} />
                </td>
                <td className="settings-actions actions">
                  <Button size="s" onClick={() => setEditing(m)} missingPermission={missing} aria-label={`Access until of ${m.email}`}>
                    Access until…
                  </Button>
                  <Button size="s" variant="danger" onClick={() => void remove(m)} missingPermission={missing} busy={busy === m.userId}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </div>
      <AddMemberDialog open={adding} kind={kind} roles={roles} defaultRole={defaultRole} ctx={ctx} onClose={() => setAdding(false)} onAdd={onAdd} />
      <AccessUntilDialog member={editing} ctx={ctx} onClose={() => setEditing(null)} onSave={onExpiryChange} />
    </div>
  );
}

function AddMemberDialog({ open, kind, roles, defaultRole, ctx, onClose, onAdd }: { open: boolean; kind: RoleKind; roles: string[]; defaultRole: string; ctx?: ZoneContext; onClose: () => void; onAdd: MembersTableProps['onAdd'] }) {
  const toasts = useToasts();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(defaultRole);
  const [until, setUntil] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setEmail('');
      setRole(defaultRole);
      setUntil(null);
      setError(null);
    }
  }, [open, defaultRole]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onAdd(email.trim(), role, secondsToIso(until));
      toasts.success(`${email.trim()} added as ${roleLabel(kind, role)}.`);
      onClose();
    } catch (err) {
      const api = asApiError(err);
      setError(api.status === 404 ? 'No account with this email; invite them instead.' : (api.fieldMessage('email') ?? api.fieldMessage('expiresAt') ?? api.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add a member" open={open} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} data-testid="add-member-dialog">
        <Field label="Email" required>
          <TextInput type="email" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
        </Field>
        <Field label="Role" required help={roleDescription(kind, role)}>
          <RoleSelect kind={kind} roles={roles} aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)} />
        </Field>
        <RoleHelp kind={kind} />
        <Field label="Access until" help="Optional; empty keeps the access until it is removed.">
          <DateTimeInput aria-label="Access until" allowEmpty ctx={ctx} value={until} onCommit={setUntil} />
        </Field>
        {error && <LoadError message={error} />}
        <div className="modal-footer form-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!email.trim()}>
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AccessUntilDialog({ member, ctx, onClose, onSave }: { member: MemberRow | null; ctx?: ZoneContext; onClose: () => void; onSave: MembersTableProps['onExpiryChange'] }) {
  const toasts = useToasts();
  const [until, setUntil] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (member) {
      setUntil(isoToSeconds(member.expiresAt));
      setError(null);
    }
  }, [member]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!member) return;
    setBusy(true);
    setError(null);
    try {
      const iso = secondsToIso(until);
      await onSave(member, iso);
      toasts.success(iso ? `${member.email} has access until the chosen time.` : `${member.email} has access with no end.`);
      onClose();
    } catch (err) {
      const api = asApiError(err);
      setError(api.isVersionConflict ? 'Someone changed this member meanwhile; the list was re-read.' : (api.fieldMessage('expiresAt') ?? api.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={member ? `Access until — ${member.email}` : 'Access until'} open={member !== null} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} data-testid="access-until-dialog">
        <Field label="Access until" help="Empty: the access has no end. A time in the past is refused.">
          <DateTimeInput aria-label="Access until" allowEmpty ctx={ctx} value={until} onCommit={setUntil} />
        </Field>
        {error && <LoadError message={error} />}
        <div className="modal-footer form-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
